import React from "react";
import { FiFileText, FiUsers, FiShield, FiAlertTriangle, FiLock, FiCheckCircle, FiAlertOctagon } from "react-icons/fi";
import { MONTHS } from "../config";

function Terms() {
    // Date Logic
    const now = new Date();
    const last_update = MONTHS[now.getMonth()] + ' ' + now.getFullYear();

    return (
        <div className="pt-24 pb-16 min-h-screen bg-yellow-600/50 relative overflow-hidden text-yellow-100">
            
            {/* Decorative Background Elements (Consistent with Privacy Page) */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-500/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                
                {/* Header */}
                <div className="mb-12 text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-yellow-500/20 border border-yellow-400/30 mb-6 shadow-lg shadow-yellow-500/20">
                        <FiFileText className="text-4xl text-yellow-100" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 text-black tracking-tight">
                        Terms of Service
                    </h1>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-400/20 rounded-full backdrop-blur-sm">
                        <FiCheckCircle className="text-yellow-300" />
                        <p className="text-black text-sm font-medium">Last updated: {last_update}</p>
                    </div>
                </div>

                {/* Content Container */}
                <div className="space-y-8">

                    {/* Introduction Card */}
                    <div className="bg-yellow-600/90 backdrop-blur-md rounded-2xl p-6 sm:p-8 shadow-xl border border-yellow-500/20">
                        <h2 className="text-2xl font-bold mb-4 text-black">General Terms and Conditions</h2>
                        <p className="text-black leading-relaxed">
                            Using any application developed by Semperor Games constitutes full acceptance of these Terms and Conditions.
                            Our goal is to provide a safe, healthy, and responsible environment for all users. Please read the following
                            terms carefully before using the applications.
                        </p>
                    </div>

                    {/* Section 1: Membership */}
                    <Section number="1" title="Membership & Usage" icon={<FiUsers />}>
                        <div className="space-y-4 text-black">
                            <p className="leading-relaxed">
                                Membership is permitted for users aged <strong className="text-yellow-900 bg-yellow-400/20 px-1 rounded">13 and above</strong>.
                                Users under 13 may register only with the consent of a legal guardian.
                            </p>
                            <p className="leading-relaxed">
                                For applications requiring registration, users must provide <span className="font-semibold">accurate, up-to-date, and real information</span>.
                            </p>
                            <div className="p-4 bg-red-700/90 border border-red-500/20 rounded-xl flex gap-3 items-start">
                                <FiAlertTriangle className="text-black mt-1 flex-shrink-0" />
                                <p className="text-rblack font-medium text-sm">
                                    Creating fake, duplicate, or impersonated accounts is strictly prohibited.
                                </p>
                            </div>
                            <p className="leading-relaxed">
                                Users are responsible for protecting their account information (passwords, etc.).
                            </p>
                        </div>
                    </Section>

                    {/* Section 2: User Responsibilities */}
                    <Section number="2" title="User Responsibilities" icon={<FiFileText />}>
                        <p className="mb-4 text-black font-medium">
                            Users must refrain from posting, sharing, or sending any content that includes:
                        </p>
                        <ListGrid items={[
                            "Immoral, obscene, violent, or offensive material",
                            "Insults, slander, threats, or mockery of others",
                            "Unauthorized advertisements or spam",
                            "Rumors, false news, or misleading information",
                            "Racist, ethnic, or provocative content",
                            "Content violating national laws or intellectual property",
                            "Anti-religious or anti-government material",
                            "Threats, intimidation, or impersonation",
                            "Altering, simulating, or hacking the product",
                            "Damaging in-game content (profiles, images)",
                            "Copying, modifying, or selling developer content"
                        ]} />
                        <p className="mt-6 text-black/80 text-sm italic border-l-4 border-yellow-500 pl-3">
                            Any misuse of the game's features to deceive, threaten, or harass others is strictly prohibited.
                        </p>
                    </Section>

                    {/* Section 3: Social & Hidden-Role Rules */}
                    <Section number="3" title="Game Specific Rules" icon={<FiShield />}>
                        <div className="space-y-3">
                            <ListItem>Inappropriate usernames may result in <span className="font-bold">permanent suspension</span>.</ListItem>
                            <ListItem>Any insult or disrespect toward other players is misconduct.</ListItem>
                            <ListItem>Disrespect toward any ethnicity or religion results in a <span className="font-bold text-red-700">permanent ban</span>.</ListItem>
                            <ListItem>These games are intended for users aged <span className="font-bold">18+</span>.</ListItem>
                            <ListItem>Political discussions are strictly forbidden.</ListItem>
                            <ListItem>All game assets are property of Semperor Studio; unauthorized use is actionable.</ListItem>
                            <ListItem>Swearing or forcing others to swear is a violation.</ListItem>
                            <ListItem>Declaring or assigning roles to oneself/others (metagaming) is prohibited.</ListItem>
                            <ListItem>One account per person.</ListItem>
                            <ListItem>Collusion or cheating leads to suspension.</ListItem>
                        </div>
                    </Section>

                    {/* Section 4: Developer Responsibilities */}
                    <Section number="4" title="Developer Responsibilities" icon={<FiLock />}>
                        <div className="space-y-3">
                            <ListItem>We strive to provide a safe environment for all users.</ListItem>
                            <ListItem>Users are solely responsible for content they create/share.</ListItem>
                            <ListItem>We reserve the right to restrict access or suspend violating accounts.</ListItem>
                            <ListItem>Continued use after updates implies acceptance of new terms.</ListItem>
                        </div>
                    </Section>

                    {/* Section 5: Privacy Policy */}
                    <Section number="5" title="Privacy Policy" icon={<FiCheckCircle />}>
                        <div className="space-y-3">
                            <ListItem>User data is treated as <span className="font-bold">confidential</span>.</ListItem>
                            <ListItem>Login details/phone numbers are not shared without consent (unless legally required).</ListItem>
                            <ListItem>We bear no responsibility for user negligence regarding account data.</ListItem>
                            <ListItem>Games may contain advertisements; types/placement may change.</ListItem>
                        </div>
                    </Section>

                    {/* Section 6: Violations */}
                    <Section number="6" title="Violations & Consequences" icon={<FiAlertOctagon />} isWarning={true}>
                        <p className="mb-4 text-black">
                            Depending on the severity of the violation, the following actions may be taken:
                        </p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {["Content removal", "Restricted access to features", "Temporary/Permanent suspension", "Legal action (if necessary)"].map((item, i) => (
                                <div key={i} className="flex items-center gap-3 bg-red-700/90 border border-red-500/20 p-3 rounded-lg">
                                    <FiAlertTriangle className="text-black flex-shrink-0" />
                                    <span className="text-black font-semibold">{item}</span>
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* Footer Note */}
                    <div className="mt-12 text-center">
                        <p className="text-black/70 mb-2">
                            By using our applications, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
                        </p>
                        <a href="mailto:contact@meerkadogames.com" className="text-yellow-900 font-bold hover:text-yellow-700 underline transition-colors">
                            Contact Support
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- Reusable UI Components (Matches Privacy Page) ---

function Section({ number, title, icon, children, isWarning = false }) {
    const borderColor = isWarning ? "border-red-900" : "border-yellow-500/20";
    const numberColor = isWarning ? "text-red-900" : "text-yellow-300";
    
    return (
        <div className={`bg-yellow-600/90 backdrop-blur-md rounded-2xl p-6 sm:p-8 shadow-xl border ${borderColor}`}>
            <div className="flex items-center gap-4 mb-6">
                <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-black/5 flex items-center justify-center border border-black/5 text-black text-xl`}>
                    {icon}
                </div>
                <h2 className="text-2xl font-bold text-black flex items-center gap-3">
                    <span className={`opacity-40 text-lg font-normal ${numberColor}`}>#{number}</span> {title}
                </h2>
            </div>
            <div className="text-black">
                {children}
            </div>
        </div>
    );
}

function ListItem({ children }) {
    return (
        <div className="flex items-start gap-3">
            <span className="text-yellow-500 mt-1.5 text-xs">●</span>
            <span className="text-black leading-relaxed">{children}</span>
        </div>
    );
}

function ListGrid({ items }) {
    return (
        <div className="grid gap-2 bg-black/10 rounded-xl p-4 border border-white/5">
            {items.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                    <span className="text-yellow-600 mt-1">▸</span>
                    <span className="text-black">{item}</span>
                </div>
            ))}
        </div>
    );
}

export default Terms;